/**
 * Sistema de caché optimizado para llamadas a Supabase
 * Evita llamadas repetidas durante un período de tiempo determinado
 * 
 * Estrategias de TTL:
 * - Datos estáticos (perfiles, eventos pasados): 5 minutos
 * - Datos dinámicos (eventos próximos, registros): 30 segundos
 * - Datos de administración: 10 segundos
 * - Datos de conteo/estadísticas: 1 minuto
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number; // Time to live en milisegundos
  hits: number; // Contador de aciertos
  lastAccessed: number; // Última vez que se accedió
}

// TTLs predefinidos según tipo de dato
// Reducidos para evitar cacheo excesivo
export const CACHE_TTL = {
  // Datos estáticos - cambian raramente
  STATIC: 1 * 60 * 1000, // 1 minuto (reducido de 5 minutos) (perfiles, eventos pasados)
  
  // Datos dinámicos - cambian frecuentemente
  DYNAMIC: 15 * 1000, // 15 segundos (reducido de 30 segundos) (eventos próximos, registros activos)
  
  // Datos de administración - necesitan ser muy frescos
  ADMIN: 5 * 1000, // 5 segundos (reducido de 10 segundos) (listados de admin)
  
  // Estadísticas y conteos
  STATS: 30 * 1000, // 30 segundos (reducido de 1 minuto) (conteos de asistentes)
  
  // Datos de usuario específico
  USER: 30 * 1000, // 30 segundos (reducido de 2 minutos) (perfil del usuario, registros del usuario)
} as const;

class SupabaseCache {
  private cache: Map<string, CacheEntry<any>> = new Map();
  private defaultTTL: number = CACHE_TTL.DYNAMIC; // 15 segundos por defecto
  private maxSize: number = 50; // Reducido de 100 a 50 para evitar cacheo excesivo
  private stats = {
    hits: 0,
    misses: 0,
    sets: 0,
    invalidations: 0,
  };

  /**
   * Obtener datos del caché
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.misses++;
      return null;
    }
    
    // Verificar si el caché ha expirado
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      this.stats.misses++;
      return null;
    }
    
    // Actualizar estadísticas
    entry.hits++;
    entry.lastAccessed = now;
    this.stats.hits++;
    
    return entry.data as T;
  }

  /**
   * Guardar datos en el caché
   */
  set<T>(key: string, data: T, ttl?: number): void {
    // Si el caché está lleno, eliminar la entrada menos usada (LRU)
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }
    
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL,
      hits: 0,
      lastAccessed: Date.now(),
    });
    
    this.stats.sets++;
  }
  
  /**
   * Eliminar la entrada menos recientemente usada (LRU)
   */
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestAccess = Infinity;
    
    for (const [key, entry] of this.cache.entries()) {
      if (entry.lastAccessed < oldestAccess) {
        oldestAccess = entry.lastAccessed;
        oldestKey = key;
      }
    }
    
    if (oldestKey) {
      this.cache.delete(oldestKey);
    }
  }

  /**
   * Invalidar una entrada del caché
   */
  invalidate(key: string): void {
    if (this.cache.delete(key)) {
      this.stats.invalidations++;
    }
  }

  /**
   * Invalidar todas las entradas que coincidan con un patrón
   */
  invalidatePattern(pattern: string): void {
    // Glob simple: * = cualquier secuencia; el resto de caracteres regex se escapan.
    let regexPattern = '';
    for (const char of pattern) {
      if (char === '*') {
        regexPattern += '.*';
      } else if (char === '?') {
        regexPattern += '.';
      } else if ('\\^$+.|()[]{}|'.includes(char)) {
        regexPattern += `\\${char}`;
      } else {
        regexPattern += char;
      }
    }

    const regex = new RegExp(`^${regexPattern}$`);
    const keysToDelete: string[] = [];
    
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        keysToDelete.push(key);
      }
    }
    
    // Eliminar todas las claves que coinciden
    keysToDelete.forEach(key => this.cache.delete(key));
    
    if (keysToDelete.length > 0) {
      this.stats.invalidations += keysToDelete.length;
    }
  }

  /**
   * Limpiar todo el caché
   */
  clear(): void {
    this.cache.clear();
    // Resetear estadísticas al limpiar
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      invalidations: 0,
    };
  }
  
  /**
   * Limpiar todo el caché y forzar recarga en la próxima petición
   * Útil cuando se necesita datos completamente frescos
   */
  forceClear(): void {
    this.clear();
    // En web, también limpiar cualquier caché del navegador si es posible
    if (typeof window !== 'undefined' && 'caches' in window) {
      // Limpiar service worker caches si existen
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      }).catch(() => {
        // Ignorar errores de limpieza de caché
      });
    }
  }
  
  /**
   * Obtener estadísticas del caché
   */
  getStats() {
    return {
      ...this.stats,
      size: this.cache.size,
      maxSize: this.maxSize,
      hitRate: this.stats.hits + this.stats.misses > 0 
        ? (this.stats.hits / (this.stats.hits + this.stats.misses) * 100).toFixed(2) + '%'
        : '0%',
    };
  }
  
  /**
   * Limpiar entradas expiradas
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > entry.ttl) {
        this.cache.delete(key);
      }
    }
  }
}

// Instancia singleton del caché
export const supabaseCache = new SupabaseCache();

// Limpiar caché expirado cada 1 minuto (más frecuente)
if (typeof setInterval !== 'undefined') {
  setInterval(() => {
    supabaseCache.cleanup();
  }, 1 * 60 * 1000);
}

/**
 * Helper para generar claves de caché consistentes
 */
export function getCacheKey(prefix: string, ...params: (string | number | undefined)[]): string {
  return `${prefix}:${params.filter(p => p !== undefined).join(':')}`;
}
